import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

/**
 * Pricing tier display for the product detail page.
 *
 * Uses the cart's preview rules so the highlighted tier matches what the
 * cart will actually charge after "Add to cart" — not stale per-product
 * thresholds that ignored cart aggregation.
 */
const PricingTiers = ({ product, quantity, setQuantity }) => {
  const { t } = useLanguage();
  const {
    getPreviewPrice,
    SEMI_GROS_MIN_TOTAL,
    GROS_MIN_TOTAL,
    PRODUCT_GROS_MIN,
  } = useCart();

  const preview = getPreviewPrice(product, quantity);
  const activeTier = preview.tier; // 'detail' | 'semi_gros' | 'gros'

  const tiers = [
    {
      key: 'detail',
      name: t('tier_detail') || 'Détail',
      price: product.price_detail,
      oldPrice: product.price_detail_original,
      // Click to set qty=1 → falls under detail when cart is empty
      jumpQty: 1,
      description: t('tier_unit_price') || 'Prix unitaire',
    },
    {
      key: 'semi_gros',
      name: t('tier_semi_wholesale') || 'Semi-gros',
      price: product.price_semi_gros,
      oldPrice: product.price_semi_gros_original,
      // Click → fill the cart up to the SEMI_GROS_MIN_TOTAL threshold with this product
      jumpQty: SEMI_GROS_MIN_TOTAL,
      description: `À partir de ${SEMI_GROS_MIN_TOTAL} articles au panier`,
    },
    {
      key: 'gros',
      name: t('tier_wholesale') || 'Gros',
      price: product.price_gros,
      oldPrice: product.price_gros_original,
      // Click → quickest way to reach GROS = take 12 of the same product
      jumpQty: PRODUCT_GROS_MIN,
      description: `À partir de ${PRODUCT_GROS_MIN} unités d'un même produit ou ${GROS_MIN_TOTAL} articles au panier`,
    },
  ];

  const handleTierClick = (jumpQty) => setQuantity(jumpQty);

  const activeTierData = tiers.find((tt) => tt.key === activeTier) || tiers[0];

  return (
    <div className="space-y-4">
      <h4 className="text-xs uppercase tracking-widest text-muted mb-3">
        {t('tier_price_by_qty')}
      </h4>

      <div className="grid grid-cols-3 gap-2">
        {tiers.map((tier) => {
          const isActive = tier.key === activeTier;
          return (
            <button
              key={tier.key}
              type="button"
              onClick={() => handleTierClick(tier.jumpQty)}
              className={`p-4 border transition-all duration-300 text-center ${
                isActive
                  ? 'border-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]'
                  : 'border-theme hover:border-[color:rgb(var(--primary-rgb)/0.30)]'
              }`}
              data-testid={`tier-${tier.key}`}
            >
              <p className={`text-xs uppercase tracking-wider mb-1 ${
                isActive ? 'text-[color:var(--color-primary)]' : 'text-muted'
              }`}>
                {tier.name}
              </p>
              {tier.oldPrice && (
                <p className="text-xs text-muted line-through" data-testid={`tier-old-price-${tier.key}`}>{tier.oldPrice}€</p>
              )}
              <p className={`text-xl font-light ${
                tier.oldPrice ? 'text-red-400' : isActive ? 'text-heading' : 'text-body'
              }`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {tier.price}€
              </p>
              <p className="text-xs text-muted mt-1">{tier.description}</p>
            </button>
          );
        })}
      </div>

      <div className="card-surface p-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted text-sm">{t('tier_current_price')}</p>
            <p className="text-xs text-muted uppercase tracking-wider">{activeTierData.name}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl text-[color:var(--color-primary)] font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {preview.unit.toFixed(2)}€
            </p>
            <p className="text-sm text-muted">
              {t('cart_total')}: <span className="text-heading">{preview.total.toFixed(2)}€</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTiers;
